import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Flame, TrendingUp, TrendingDown } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import VolatilityHeatmap from "@/components/terminal/VolatilityHeatmap";
import { signTone, toneClassName, toneVar, signedPrefix } from "@/lib/numberColor";

/**
 * Public /watch page — the marketing/acquisition edge of the volatility engine.
 *
 * No auth. Shows:
 *  - Full asset-class heatmap (all 20 instruments)
 *  - Live tape of the top-3 hottest instruments with sparkline + live price
 *  - Massive "Sign up to trade" CTA
 *
 * All data comes from public endpoints (/api/market/volatility, /api/market/ticker,
 * /api/market/history) — no cookie or JWT required.
 */

const CHIP_STYLES = {
    calm: "bg-muted/60 text-muted-foreground border-border",
    normal: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/40",
    elevated: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
    hot: "bg-danger/15 text-danger border-danger/40",
};

export default function Watch() {
    const [top3, setTop3] = useState([]);
    const [tickerData, setTickerData] = useState({});   // symbol → {price, change_pct, asset_class}
    const [histories, setHistories] = useState({});     // symbol → [{t,p}, ...]

    // Refresh loop: heatmap → pick top 3 hottest → fetch their live price + history.
    // Memoised so useEffect deps are stable and the linter doesn't nag.
    const loadTop3 = useCallback(async () => {
        try {
            const { data } = await api.get("/market/volatility");
            const three = (data.items || []).slice(0, 3);
            setTop3(three);

            // Parallel-fetch price + history for each hot instrument.
            const results = await Promise.all(three.map(async (it) => {
                const [t, h] = await Promise.all([
                    api.get(`/market/ticker/${it.symbol}`).catch(() => ({ data: null })),
                    api.get(`/market/history/${it.symbol}?points=40`).catch(() => ({ data: null })),
                ]);
                return { symbol: it.symbol, ticker: t.data, history: h.data?.points || [] };
            }));

            const newTickers = {};
            const newHistories = {};
            for (const r of results) {
                if (r.ticker) newTickers[r.symbol] = r.ticker;
                newHistories[r.symbol] = r.history.map((x) => ({ p: x.p, t: x.t }));
            }
            setTickerData(newTickers);
            setHistories(newHistories);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.debug("Watch loadTop3 failed:", e);
        }
    }, []);

    useEffect(() => {
        loadTop3();
        const id = setInterval(loadTop3, 15_000);
        return () => clearInterval(id);
    }, [loadTop3]);

    return (
        <div className="min-h-screen bg-background text-foreground" data-testid="watch-page">
            {/* Simple public header */}
            <header className="border-b border-border">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="watch-brand-home">
                        <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center font-black text-primary-foreground">MD</div>
                        <span className="font-extrabold tracking-tight">MELIAN DIALOGUE</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/login"
                            className="text-sm font-semibold text-muted-foreground hover:text-foreground px-3 py-1.5"
                            data-testid="watch-header-login"
                        >
                            Log in
                        </Link>
                        <Link
                            to="/register"
                            className="text-sm font-extrabold bg-primary text-primary-foreground rounded-md px-4 py-2 hover:opacity-90 transition-opacity"
                            data-testid="watch-header-signup"
                        >
                            Sign up free
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
                {/* Hero */}
                <div className="mb-8 md:mb-10">
                    <div className="inline-flex items-center gap-2 rounded-full border border-danger/40 bg-danger/5 px-3 py-1 mb-4">
                        <span className="inline-block w-2 h-2 rounded-full bg-danger animate-pulse" />
                        <span className="text-xs font-bold text-danger uppercase tracking-wider">Live · public tape</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-3" data-testid="watch-h1">
                        The Nairobi &amp; FX markets — <span className="text-primary">live, right now.</span>
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                        Session volatility across 130+ live-priced instruments spanning NSE · DSE · USE · RSE · ESX + US stocks + crypto + majors FX.
                        Realistic simulator — sign up in 60 seconds to trade with virtual capital. Free for individuals, forever.
                    </p>
                </div>

                {/* Heatmap — reused component */}
                <VolatilityHeatmap assetClass="all" onSelect={() => { /* readonly on /watch */ }} />

                {/* Live tape — top 3 */}
                <section className="mt-6" data-testid="watch-live-tape">
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-5 h-5 text-danger" />
                        <h2 className="text-lg md:text-xl font-extrabold tracking-tight">Top 3 hottest right now</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        {top3.length === 0 && [0, 1, 2].map((i) => (
                            <div key={`skeleton-${i}`} className="border border-border rounded-xl p-4 h-40 bg-card animate-pulse" />
                        ))}
                        {top3.map((it, idx) => (
                            <LiveTapeCard
                                key={it.symbol}
                                rank={idx + 1}
                                heatItem={it}
                                ticker={tickerData[it.symbol]}
                                history={histories[it.symbol] || []}
                            />
                        ))}
                    </div>
                </section>

                {/* Big CTA */}
                <section className="mt-10 md:mt-14 border-2 border-primary/40 bg-primary/5 rounded-2xl p-6 md:p-10 text-center" data-testid="watch-signup-cta">
                    <div className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Ready to actually trade?</div>
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
                        Trade Kenya markets on <span className="text-primary">virtual capital</span>.<br />
                        Free for individuals · forever.
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-6">
                        Kenya&rsquo;s first prop-firm evaluation platform. NSE + FX + Commodities + Options on one terminal, no personal risk.
                        Individual accounts are free forever. Institutional (MoE / CMA) seats include Obondo AI copilot + full analytics.
                    </p>
                    <Link
                        to="/register"
                        className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-extrabold text-base md:text-lg rounded-lg px-6 md:px-8 py-3 md:py-4 hover:opacity-90 hover:scale-[1.02] transition-all shadow-xl"
                        data-testid="watch-cta-signup"
                    >
                        Sign up to trade
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                    <div className="mt-4 text-xs text-muted-foreground">
                        Free forever · No card required · CMA-aligned virtual trading
                    </div>
                </section>

                {/* Compliance footer */}
                <p className="mt-8 text-[11px] text-muted-foreground text-center max-w-3xl mx-auto leading-relaxed">
                    All prices shown are synthetic ticks generated by a realistic simulator calibrated on real
                    Kenyan-market volatility patterns. 100% virtual capital · not investment advice ·
                    <Link to="/legal/terms" className="underline ml-1">Terms</Link> ·
                    <Link to="/legal/privacy" className="underline ml-1">Privacy</Link>
                </p>
            </main>
        </div>
    );
}

function LiveTapeCard({ rank, heatItem, ticker, history }) {
    const spark = useMemo(() =>
        (history || []).slice(-30).map((h) => ({ p: h.p }))
    , [history]);

    const bucketStyle = CHIP_STYLES[heatItem.heat_bucket] || CHIP_STYLES.normal;
    const changeNum = ticker?.change ?? 0;
    const changePct = ticker?.change_pct ?? 0;
    const tone = signTone(changePct);
    const dec = heatItem.asset_class === "fx" ? (heatItem.price < 5 ? 5 : 3) : 2;

    return (
        <div
            className="border border-border rounded-xl bg-card p-4 hover:border-primary/40 transition-colors"
            data-testid={`live-tape-card-${heatItem.symbol}`}
        >
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="text-xs text-muted-foreground font-bold">#{rank}</div>
                    <div className="flex items-center gap-2">
                        <span className="text-xl font-extrabold tracking-tight">{heatItem.symbol}</span>
                        {heatItem.asset_class === "fx" && (
                            <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-primary/20 text-primary font-bold">FX</span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{ticker?.sector || heatItem.asset_class.toUpperCase()}</div>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-md border ${bucketStyle}`}>
                    {heatItem.heat}×
                </span>
            </div>

            <div className="flex items-baseline justify-between mb-2">
                <div className="font-mono font-black text-xl md:text-2xl">
                    {heatItem.asset_class === "nse" ? "KES " : ""}
                    {ticker ? Number(ticker.price).toFixed(dec) : "—"}
                </div>
                <div className={`inline-flex items-center gap-1 text-sm font-mono font-bold ${toneClassName(tone)}`}>
                    {tone === "neg" ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                    {signedPrefix(changePct)}{Number(changePct).toFixed(2)}%
                </div>
            </div>

            <div className="h-14 -mx-1">
                {spark.length > 1 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={spark} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                            <YAxis domain={["dataMin", "dataMax"]} hide />
                            <Line
                                type="monotone"
                                dataKey="p"
                                stroke={toneVar(tone)}
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}
